import AccountRestrictionDTO from 'features/accounts/dtos/AccountRestrictionDTO';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import ContractualAccountDTO from 'features/contracts/dtos/ContractualAccountDTO';
import PromiseLoadingSpinner from 'features/shared/components/loaders/PromiseLoadingSpinner';
import ModalFooter from 'features/shared/components/modal/ModalFooter';
import ModalHeader from 'features/shared/components/modal/ModalHeader';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import AccountRestrictionSelect from 'features/accounts/components/AccountRestrictionSelect';
import { Nullable } from 'features/shared/types/nullable';
import Alert from 'features/shared/alerts/components/Alert';
import { AccountRestrictions_AccountRestrictionDialog } from 'features/shared/components/loaders/promiseLoadingAreas';
import { useTranslation } from 'react-i18next';

export type AccountRestrictionDialogMode = 'Add' | 'Update';

interface Props {
  mode: AccountRestrictionDialogMode;
  contracts: ContractDropdownDTO[];
  contractualAccounts: ContractualAccountDTO[];
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (restriction: AccountRestrictionDTO, mode: AccountRestrictionDialogMode) => void;
  selectedRestriction?: Nullable<AccountRestrictionDTO>;
}

const AccountRestrictionDialog = ({ mode, contracts,  contractualAccounts, show, setShow, onSubmit, selectedRestriction }: Props) => {
  // translations
  const { t } = useTranslation("shared\\terms");
  const tLabels = useTranslation("accounts\\accounts").t;

  // state
  const [actualAccountRestriction, setActualAccountRestriction] = useState<Nullable<AccountRestrictionDTO>>(null);
  const [error, setError] = useState<Nullable<string>>(null);

  // side-effects
  useEffect(() => {
    if (selectedRestriction) {
      setActualAccountRestriction(selectedRestriction);
    } else {
      // preset AccountRestrictionType to 'ContractrualAccount'
      setActualAccountRestriction({entity: "ContractualAccount"})
    }
  }, [selectedRestriction]);

  // helpers
  const validateRestriction = (): boolean => {
    if (actualAccountRestriction) {
      if (
        actualAccountRestriction.contractualAccountId !== undefined ||
        actualAccountRestriction.placeOfDeliveryId !== undefined
      ) {
        setError(null);
        return true;
      }
    }

    setError(tLabels("accountRestrictionDialog.message"));
    return false;
  };

  // handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateRestriction() && actualAccountRestriction) {
      onSubmit({...actualAccountRestriction}, mode);
      setActualAccountRestriction(null); 
    }
  };

  const closeDialogHandler = () => {
    setError(null);
    // setActualAccountRestriction(null);
    setShow(false);
  };

  // sub-renders
  const renderError = () => {
    if (error !== null) {
      return (
        <Alert alertObj={{alertType: "Danger", message: error}} className="mt-4 mb-0 mx-3" />
      );
    }
  }

  // render
  return (
    <Modal show={show} onHide={closeDialogHandler} backdrop="static">
      <ModalHeader
        title={`${t(mode)} ${tLabels("accountRestrictionDialog.restriction")}`}
        onCloseClick={closeDialogHandler}
      />
      {renderError()}
      <form onSubmit={submitFormHandler}>
        <div className="modal-body py-4">
          <AccountRestrictionSelect
            contracts={contracts}
            contractualAccounts={contractualAccounts}
            selectedRestriction={actualAccountRestriction}
            setSelectedRestrictiton={setActualAccountRestriction}
            fullInputsWidth
          />
        </div>
        <ModalFooter>
          <button type="submit" className="btn btn-primary">
            {`${t(mode)} ${tLabels("accountRestrictionDialog.restriction")}`}
          </button>
        </ModalFooter>
      </form>
      <PromiseLoadingSpinner area={AccountRestrictions_AccountRestrictionDialog} />
    </Modal>
  );
}

export default AccountRestrictionDialog;
